import { useCallback, useEffect, useMemo, useState } from "react";

import { type Cluster, PerfectScaleAgentUpgradeStatus } from "@doitintl/cmp-models";
import { Alert, Skeleton, Stack, Typography } from "@mui/material";

import { useApiContext } from "../../../../api/context";
import { CopyCodeBlock } from "../../../../Components/CopyCodeBlock/CopyCodeBlock";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { consoleErrorWithSentry } from "../../../../utils";
import { useErrorSnackbar } from "../../../Integrations/Slack/utils";
import { getAgentInstallationCommand } from "../../api";
import { ClusterAgentStatus } from "../../Clusters/Table/types";
import { getClusterAgentStatus } from "../../Clusters/Table/utils";
import { useFsk8sConfig } from "../../hooks";

const cloneChartCommand = `
helm repo add perfectscale https://perfectscale-io.github.io --force-update
`.trim();

interface InstallAgentProps {
  cluster: Cluster | undefined;
}

const InstallAgent = ({ cluster }: InstallAgentProps) => {
  const errorSnackbar = useErrorSnackbar();
  const api = useApiContext();
  const { customer } = useCustomerContext();
  const [config, versionLoading] = useFsk8sConfig();

  const [helmCommand, setHelmCommand] = useState<string>();
  const [helmCommandLoading, setHelmCommandLoading] = useState(false);
  const [helmCommandHadError, setHelmCommandHadError] = useState(false);

  const agentStatus = useMemo(() => (cluster ? getClusterAgentStatus(cluster) : undefined), [cluster]);

  const fetchHelmCommand = useCallback(async () => {
    if (!cluster?.id || helmCommand) return;

    setHelmCommandLoading(true);
    try {
      const response = await getAgentInstallationCommand(api, customer.id, cluster?.id);
      setHelmCommand(response.data);
    } catch (error) {
      consoleErrorWithSentry(error);
      errorSnackbar("Failed to fetch Helm command");
      setHelmCommandHadError(true);
    } finally {
      setHelmCommandLoading(false);
    }
  }, [api, cluster?.id, customer.id, errorSnackbar, helmCommand]);

  useEffect(() => {
    fetchHelmCommand();
  }, [fetchHelmCommand]);

  const renderAlert = () => {
    if (versionLoading) {
      return <Skeleton variant="rectangular" width="100%" height={50} />;
    }

    if (agentStatus === ClusterAgentStatus.NEVER_TRANSMITTED) {
      return (
        <Alert severity="info">
          Install this agent to dive deeper into cluster cost and utilization metrics in Kubernetes Lens. <br />
          After installing the agent it can take up to 10 minutes for the status in the assets table to update.
        </Alert>
      );
    }

    if (
      cluster?.perfectScale?.AgentUpgradeStatus &&
      [PerfectScaleAgentUpgradeStatus.UPGRADE_AVAILABLE, PerfectScaleAgentUpgradeStatus.UPGRADE_REQUIRED].includes(
        cluster?.perfectScale?.AgentUpgradeStatus
      )
    ) {
      return <Alert severity="info">Upgrade to version {config?.agentLatestTag} is now available</Alert>;
    }

    if (agentStatus === ClusterAgentStatus.TRANSMITTING) {
      return <Alert severity="success">Agent already installed</Alert>;
    }
  };

  const renderContent = () => {
    if (helmCommandLoading) {
      return <Skeleton variant="rectangular" width="100%" height={192} />;
    }

    if (helmCommandHadError) {
      return <Typography color="error.main">Failed to fetch Helm command. Please try again later</Typography>;
    }

    return <CopyCodeBlock base={helmCommand ?? ""} />;
  };

  return (
    <Stack spacing={4}>
      {renderAlert()}

      <Stack spacing={2}>
        <Typography>
          1. Clone the Helm Chart repository to a location where you have access to your Kubernetes cluster:
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Clone Helm Chart
          </Typography>
          <Typography>
            <CopyCodeBlock base={cloneChartCommand} />
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={2}>
        <Typography>2. Deploy the Helm Chart</Typography>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Deploy Helm Chart
          </Typography>
          <Typography>{renderContent()}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default InstallAgent;
